<template>
	<div>
		<!-- Submenu -->
		<policy-submenu />

		<!-- Body -->
		<core-layout-content large>
			<!-- Content -->
			<div class="flex wrap xs-12">
				<!-- Left -->
				<div class="xs-12 md-7 md-pr4">
					<!-- Particulars -->
					<policy-particulars
						:particulars="fetchedPolicy"
						@updated="fetchPolicy"
					/>

					<!-- Accepted Offer -->
					<j-card class="bg5 mt6">
						<!-- Headline -->
						<div class="p2 pt4 pb4 bb1 bc7 bg5">
							<div class="fs5 c1 fw7 lh125 mb1">{{ $t("accepted_offer") }}</div>
						</div>
						<!-- Body -->
						<div class="p4">
							<!-- Product -->
							<product-info :product="computedProduct" />

							<!-- Premium -->
							<premium-info :pricing="computedPricing" class="mt6" />

							<!-- Exclusions -->
							<exclusions-info
								:policyId="fetchedPolicy.id"
								:list="computedExclusions"
								class="mt6"
							/>

							<!-- Benefit Options -->
							<policy-benefit-options
								:list="computedBenefitOptions"
								class="mt6"
							/>
						</div>
					</j-card>

					<!-- Benefit summary -->
					<benefit-summary :policyId="id" class="mt6" />

					<!-- Holder -->
					<main-holder :holder="computedHolder" class="mt6" />

					<!-- Other Holders -->
					<other-holders :list="computedOtherHolders" class="mt6" />

					<!-- Insured People -->
					<insured-people :list="computedInsuredPeople" class="mt6" />

					<!-- Insured Objects -->
					<!-- TODO: implement view for contractInsured __typename === "object" -->
					<insured-objects :list="computedInsuredObjects" class="mt6" />

					<!-- Payment Info -->
					<payment-infos
						:list="computedPaymentInfo"
						class="mt6"
						@removed="fetchPolicy"
						@added="fetchPolicy"
						@updated="fetchPolicy"
					/>
				</div>

				<!-- Right -->
				<div class="xs-12 md-5 md-pl4">
					<!-- Info Widget -->
					<core-info-card :data="computedInfo" />

					<!-- Policy actions -->
					<policy-actions
						v-if="$store.state.settings.policies.actions.hasActions"
						v-can="'writePolicies'"
						class="mt6"
						:policyId="fetchedPolicy.id"
						:policyNumber="fetchedPolicy.issuerNumber"
						:amount="computedPricing.amount"
						:currencyCode="computedPricing.currencyCode"
						:context="fetchedPolicy"
					/>

					<!-- Info about cancellation -->
					<cancel-policy-info
						class="mt6"
						v-if="fetchedPolicy.status === 'CANCELLED'"
						:cancellationReason="fetchedPolicy.cancellationReason"
					/>

					<!-- Notes -->
					<core-notes-card
						class="mt6"
						notesOf="policy"
						:id="fetchedPolicy.id"
						:list="computedNotes"
						:canWrite="canWritePolicyNotes"
						@note:created="fetchPolicy"
						@note:updated="fetchPolicy"
						@note:deleted="fetchPolicy"
					/>

					<!-- Files -->
					<core-files-card
						class="mt6"
						:id="fetchedPolicy.id"
						filesOf="policy"
						:list="computedAttachments"
						:canWrite="canWriteFiles"
						@file:deleted="fetchPolicy"
						@files:uploaded="fetchPolicy"
					/>
				</div>
			</div>

			<!-- Bottom menu -->
			<div
				class="flex wrap xs-12 xs-right bt1 bc7 pt2 mt8"
				v-can="'writePolicies'"
			>
				<!-- Cancel -->
				<j-button
					v-if="$store.state.settings.policies.cancelling.hasCancel"
					type="secondary"
					@click="$modal.show('cancelPolicy')"
					action="showCancelPolicyModal"
					>{{ $t("cancel_policy") }}</j-button
				>
				<!-- Delete -->
				<j-button
					type="error"
					@click="$modal.show('j-dialog')"
					v-can="'writePolicies'"
					class="ml2"
					action="showDeletePolicyModal"
				>
					<crm-icon name="delete" size="normal" color="c5" class="mr1" />
					{{ $t("delete_policy") }}
				</j-button>
			</div>

			<!-- Cancel Policy Popup -->
			<core-modal name="cancelPolicy" :headline="$t('cancel_policy')">
				<cancel-policy v-bind="fetchedPolicy" @cancelled="afterCancellation" />
			</core-modal>
		</core-layout-content>

		<!-- Delete Confirmation Modal -->
		<j-dialog
			:title="$t('delete_policy')"
			type="delete"
			@confirm="onDelete(id)"
			>{{ $t("errors.are_you_sure_you_want_to_delete_policy") }}</j-dialog
		>

		<!-- Loading toast -->
		<j-loading-toast v-if="isLoading" />
	</div>
</template>

<script>
import PolicySubmenu from "../components/PolicySubmenu"
import JButton from "@/components/JButton/"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreModal from "@/modules/core/components/CoreModal"
import MainHolder from "../components/MainHolder"
import ProductInfo from "../components/ProductInfo"
import PremiumInfo from "../components/PremiumInfo"
import OtherHolders from "../components/OtherHolders"
import InsuredPeople from "../components/InsuredPeople"
import InsuredObjects from "../components/InsuredObjects"
import PaymentInfos from "../components/PaymentInfos"
import CancelPolicyInfo from "../components/CancelPolicyInfo"
import CoreNotesCard from "@/modules/core/components/CoreNotesCard/index.vue"
import JLoadingToast from "@/components/JLoadingToast"
import PolicyParticulars from "../components/PolicyParticulars"
import CancelPolicy from "../components/CancelPolicy"
import CoreFilesCard from "@/modules/core/components/CoreFilesCard"
import CoreInfoCard from "@/modules/core/components/CoreInfoCard"
import CrmIcon from "@/components/CrmIcon"
import PolicyActions from "../components/PolicyActions"
import ExclusionsInfo from "../components/ExclusionsInfo"
import PolicyBenefitOptions from "../components/PolicyBenefitOptions"
import BenefitSummary from "../components/BenefitSummary"
import JCard from "@/components/JCard"
import api from "../api/"
import get from "lodash.get"
import JDialog from "@/components/JDialog/"
import { can } from "@/permissions/"

export default {
	components: {
		PolicySubmenu,
		JButton,
		CoreLayoutContent,
		CoreModal,
		OtherHolders,
		InsuredPeople,
		InsuredObjects,
		MainHolder,
		PaymentInfos,
		CoreNotesCard,
		JLoadingToast,
		PolicyParticulars,
		CoreFilesCard,
		CoreInfoCard,
		ProductInfo,
		PremiumInfo,
		CancelPolicy,
		CrmIcon,
		CancelPolicyInfo,
		PolicyActions,
		ExclusionsInfo,
		PolicyBenefitOptions,
		BenefitSummary,
		JCard,
		JDialog
	},
	props: {
		id: { type: String, required: true }
	},
	data() {
		return {
			isLoading: false,
			fetchedPolicy: {}
		}
	},
	computed: {
		computedHolder() {
			return this.fetchedPolicy.contractHolder
				? this.fetchedPolicy.contractHolder
				: {}
		},
		computedOffers() {
			return this.fetchedPolicy.offers ? this.fetchedPolicy.offers : []
		},
		computedExclusions() {
			return this.fetchedPolicy.acceptedOffer
				? this.fetchedPolicy.acceptedOffer.exclusions
				: this.fetchedPolicy.exclusions
		},
		computedOtherHolders() {
			return this.fetchedPolicy.otherContractHolders
				? this.fetchedPolicy.otherContractHolders
				: []
		},
		computedInfo() {
			return {
				status: this.fetchedPolicy.status,
				createdAt: this.fetchedPolicy.createdAt,
				createdBy: this.fetchedPolicy.createdBy,
				lastModifiedAt: this.fetchedPolicy.lastModifiedAt,
				lastModifiedBy: this.fetchedPolicy.lastModifiedBy
			}
		},
		computedProduct() {
			return this.fetchedPolicy.acceptedOffer
				? this.fetchedPolicy.acceptedOffer.product
				: this.fetchedPolicy.product
		},
		computedPricing() {
			return this.fetchedPolicy.acceptedOffer
				? this.fetchedPolicy.acceptedOffer.premium
				: this.fetchedPolicy.premium
		},
		computedNotes() {
			return this.fetchedPolicy.notes ? this.fetchedPolicy.notes : []
		},
		computedPaymentInfo() {
			return this.fetchedPolicy.paymentInfos
				? this.fetchedPolicy.paymentInfos
				: []
		},
		computedAttachments() {
			return this.fetchedPolicy.attachments
				? this.fetchedPolicy.attachments
				: []
		},
		computedInsuredPeople() {
			const insureds = get(this, "fetchedPolicy.contractInsured")
			if (!insureds) return []
			// Filter out only insured people, ignore objects
			return insureds.filter(item => item.__typename === "individual")
		},
		computedInsuredObjects() {
			const insureds = get(this, "fetchedPolicy.contractInsured")
			if (!insureds) return []
			// Filter out only insured objects, ignore individuals
			return insureds.filter(item => item.__typename === "object")
		},
		computedBenefitOptions() {
			const benefitOptions =
				get(this, "fetchedPolicy.acceptedOffer.benefitOptions") ||
				get(this, "fetchedPolicy.benefitOptions")
			if (!benefitOptions) return []
			return benefitOptions
		},
		canWritePolicyNotes() {
			return can("writePolicyNotes")
		},
		canWriteFiles() {
			return can("writeFiles")
		}
	},
	methods: {
		async fetchPolicy() {
			this.isLoading = true
			const res = await api.policy({
				where: { id: this.id }
			})
			this.isLoading = false
			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}
			// If it doesn't exist, redirect
			if (res.data.policies.list.length === 0)
				this.$router.push({ name: "PoliciesList" })
			// Assign
			this.fetchedPolicy = res.data.policies.list[0]
		},
		afterCancellation() {
			this.fetchPolicy()
			this.$modal.hide("cancelPolicy")
		},
		async onDelete(id) {
			this.isLoading = true
			const res = await api.deletePolicy({ id })
			this.isLoading = false
			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}
			// go back to previous page
			if (res) this.$router.push(`/${this.$store.state.tenantId}/policies/`)
			this.$store.dispatch("addMessage", {
				message: {
					type: "success",
					text: this.$t("policy_deleted")
				}
			})
		}
	},
	created() {
		// Check if we are receiving all props
		// If not, redirect to products listing
		if (!this.id) {
			this.$router.push({ name: "PoliciesList" })
			return
		}
		// Fetch product info
		this.fetchPolicy()
	}
}
</script>
